import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  RefreshCw, TrendingUp, MousePointer, Eye, DollarSign, 
  Target, ArrowLeft, AlertCircle, Calendar, Play, Pause
} from 'lucide-react';
import * as api from '../services/api';
import { toast } from 'sonner';

const CampaignDetail = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaignData, setCampaignData] = useState(null);
  const [dateRange, setDateRange] = useState('LAST_30_DAYS');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCampaignDetails();
  }, [campaignId, dateRange]);

  const loadCampaignDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGoogleAdsCampaignDetails(campaignId, null, dateRange);
      setCampaignData(data);
    } catch (err) {
      console.error('Error loading campaign details:', err);
      setError(err.response?.data?.detail || 'Error al cargar detalles de la campaña');
      toast.error('Error al cargar detalles');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-CO').format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ENABLED: { label: 'Activa', color: 'bg-green-100 text-green-800', icon: Play },
      PAUSED: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-800', icon: Pause },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const dateRangeOptions = [
    { value: 'LAST_7_DAYS', label: 'Últimos 7 días' },
    { value: 'LAST_14_DAYS', label: 'Últimos 14 días' },
    { value: 'LAST_30_DAYS', label: 'Últimos 30 días' },
    { value: 'THIS_MONTH', label: 'Este mes' },
    { value: 'LAST_MONTH', label: 'Mes anterior' },
    { value: 'LAST_90_DAYS', label: 'Últimos 90 días' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Cargando detalles de la campaña...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/campaigns/adwords')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Campañas
        </Button>
        
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Error</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Button onClick={loadCampaignDetails}>Reintentar</Button>
        </div>
      </div>
    );
  }

  const { campaign, dailyMetrics, totals } = campaignData || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/campaigns/adwords')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{campaign?.name}</h1>
              {campaign?.status && getStatusBadge(campaign.status)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Canal: {campaign?.channelType} | ID: {campaignId}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={loadCampaignDetails} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {totals && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-100">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Impresiones</p>
                  <p className="text-xl font-bold">{formatNumber(totals.impressions)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-green-100">
                  <MousePointer className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Clics</p>
                  <p className="text-xl font-bold">{formatNumber(totals.clicks)}</p>
                  <p className="text-xs text-slate-400">CTR: {totals.ctr}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-purple-100">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Costo Total</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.cost)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-indigo-100">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">CPC Promedio</p>
                  <p className="text-xl font-bold">{formatCurrency(totals.avgCpc)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-100">
                  <Target className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Conversiones</p>
                  <p className="text-xl font-bold">{Math.round(totals.conversions)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Métricas por Día
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!dailyMetrics || dailyMetrics.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay datos para el período seleccionado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Impresiones</TableHead>
                    <TableHead className="text-right">Clics</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Conversiones</TableHead>
                    <TableHead className="text-right">Tasa Conv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyMetrics.map((day, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {formatDate(day.date)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(day.impressions)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(day.clicks)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {day.ctr}%
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(day.cost)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(day.avgCpc)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {Math.round(day.conversions)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {day.conversionRate}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignDetail;
